import React, { useEffect, useState } from "react";
import { makeRequest2 } from "../../../utils/makeRequest";
import {
  addDigitaxProfileURL,
  getDigitaxProfilesURL,
  addDigitaxAPIkeyURL,
  getDigitaxConfigURL,
  testDigitaxConnectionURL
} from "../../../utils/urls";
import { toastify } from "../../../utils/toast";
import { Password } from "primereact/password";

const DigitaxTab = ({ facilityId }) => {
  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState("");

  const [profiles, setProfiles] = useState([]);
  const [profileName, setProfileName] = useState("");
  const [profileApiKey, setProfileApiKey] = useState("");

  const [connected, setConnected] = useState(false);
  const [connectedAt, setConnectedAt] = useState("");
  const [lastTestedAt, setLastTestedAt] = useState("");
  const [lastError, setLastError] = useState("");

  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {

    if (facilityId) {
      fetchConfiguration();
      fetchProfiles();
    }

  }, [facilityId]);

  const fetchConfiguration = async () => {
    try {
      const response = await makeRequest2(
        `${getDigitaxConfigURL}/${facilityId}`,
        "GET"
      );

      console.log("Digitax config response:", response);

      if (response.success) {
        const data = response.data.digitax || {};

        setEnabled(data.enabled || false);
        setApiKey(data.apiKey || "");
        setConnected(data.connected || false);
        setConnectedAt(data.connectedAt || "");
        setLastTestedAt(data.lastTestedAt || "");
        setLastError(data.lastError || "");
        setProfiles(data.profiles || []);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleSave = async () => {
    if (enabled && !apiKey.trim() && profiles.length === 0) {
      toastify("Please enter the DigiTax API Key or add a DigiTax profile", "error");
      return;
    }

    try {
      setLoading(true);

      const response = await makeRequest2(
        `${addDigitaxAPIkeyURL}/${facilityId}`,
        "POST",
        {
          enabled,
          apiKey,
          profiles
        }
      );

      console.log("Facility id", facilityId)

      console.log("Response DIGITAX", response)

      if (response.success) {
        toastify("Configuration saved successfully", "success");
        fetchConfiguration();
      } else {
        toastify(
          response.message || "Failed to save configuration",
          "error"
        );
      }
    } catch (err) {
      toastify(err.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!apiKey.trim()) {
      toastify("Please enter the API Key", "error");
      return;
    }

    try {
      setLoading(true);

      const response = await makeRequest2(
        `${testDigitaxConnectionURL}/${facilityId}`,
        "POST",
        {
          apiKey
        }
      );

      if (response.success) {
        toastify("Connection successful", "success");
        fetchConfiguration();
      } else {
        toastify(
          response.message || "Connection failed",
          "error"
        );
      }
    } catch (err) {
      toastify(err.message || "Connection failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddProfile = async () => {
    if (!profileName.trim()) {
      toastify("Please enter a profile name", "error");
      return;
    }

    if (!profileApiKey.trim()) {
      toastify("Please enter the profile API Key", "error");
      return;
    }

    const duplicate = profiles.some(
      (profile) =>
        profile?.name?.toLowerCase() ===
        profileName.trim().toLowerCase()
    );

    if (duplicate) {
      toastify(
        "A profile with this name already exists",
        "error"
      );
      return;
    }

    try {
      setProfileLoading(true);

      const response = await makeRequest2(
        `${addDigitaxProfileURL}/${facilityId}`,
        "POST",
        {
          name: profileName.trim(),
          apiKey: profileApiKey.trim()
        }
      );

      console.log("ADD DIGITAX PROFILE RESPONSE:", response);

      if (response.success) {
        toastify(
          "DigiTax profile added successfully",
          "success"
        );

        // Reload profiles directly from the database
        await fetchConfiguration();

        // Clear form
        setProfileName("");
        setProfileApiKey("");
      } else {
        toastify(
          response.message ||
          "Failed to add DigiTax profile",
          "error"
        );
      }

    } catch (err) {
      console.error("Add DigiTax profile error:", err);

      toastify(
        err.message ||
        "Failed to add DigiTax profile",
        "error"
      );
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchProfiles = async () => {
    try {
      const response = await makeRequest2(
        `${getDigitaxProfilesURL}/${facilityId}`,
        "GET"
      );

      console.log("DigiTax profiles response:", response);

      if (response.success) {
        setProfiles(response.data?.profiles || []);
      } else {
        setProfiles([]);
        toastify(
          response.message || "Failed to load DigiTax profiles",
          "error"
        );
      }
    } catch (err) {
      console.error("Fetch DigiTax profiles error:", err);
      setProfiles([]);
    }
  };

  const handleRemoveProfile = (index) => {
    if (!window.confirm("Remove this DigiTax profile?")) {
      return;
    }

    setProfiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleToggleProfile = (index) => {
    setProfiles((prev) =>
      prev.map((profile, i) =>
        i === index
          ? {
            ...profile,
            enabled: !profile.enabled
          }
          : profile
      )
    );
  };

  const handleTestProfile = async (profile, index) => {
    if (!profile.apiKey?.trim()) {
      toastify("This profile does not have an API Key", "error");
      return;
    }

    try {
      setProfileLoading(true);

      const response = await makeRequest2(
        `${testDigitaxConnectionURL}/${facilityId}`,
        "POST",
        {
          apiKey: profile.apiKey
        }
      );

      if (response.success) {
        setProfiles((prev) =>
          prev.map((item, i) =>
            i === index
              ? {
                ...item,
                connected: true,
                connectedAt: new Date().toISOString(),
                lastTestedAt: new Date().toISOString(),
                lastError: null
              }
              : item
          )
        );

        toastify(
          `${profile.name} connection successful`,
          "success"
        );
      } else {
        setProfiles((prev) =>
          prev.map((item, i) =>
            i === index
              ? {
                ...item,
                connected: false,
                lastTestedAt: new Date().toISOString(),
                lastError:
                  response.message || "Connection failed"
              }
              : item
          )
        );

        toastify(
          response.message || "Connection failed",
          "error"
        );
      }
    } catch (err) {
      setProfiles((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
              ...item,
              connected: false,
              lastTestedAt: new Date().toISOString(),
              lastError:
                err.message || "Connection failed"
            }
            : item
        )
      );

      toastify(
        err.message || "Connection failed",
        "error"
      );
    } finally {
      setProfileLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";

    try {
      return new Date(date).toLocaleString();
    } catch {
      return "-";
    }
  };

  return (
    <div className="row">

      {/* Configuration */}
      <div className="col-lg-6 mb-3">
        <div className="card h-100">
          <div className="card-body">

            <h5 className="mb-4">DigiTax Configuration</h5>

            <div className="form-check form-switch mb-4">
              <input
                className="form-check-input"
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />

              <label className="form-check-label ms-2">
                Enable DigiTax Integration
              </label>
            </div>

            <div className="mb-3">
              <label className="form-label">
                API Key
              </label>

              <Password
                className="w-100"
                inputClassName="w-100"
                feedback={false}
                toggleMask
                value={apiKey}
                disabled={!enabled}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Paste DigiTax API Key"
              />

              <small className="text-muted">
                This API Key will be used for invoice, customer and item
                synchronization.
              </small>
            </div>

            <div className="text-end mt-4">

              <button
                className="btn btn-outline-primary me-2"
                disabled={!enabled || loading}
                onClick={handleTestConnection}
              >
                Test Connection
              </button>

              <button
                className="btn btn-primary"
                disabled={loading}
                onClick={handleSave}
              >
                {loading ? "Saving..." : "Save Configuration"}
              </button>

            </div>

          </div>
        </div>
      </div>

      {/* Status */}
      <div className="col-lg-6 mb-3">
        <div className="card h-100">
          <div className="card-body">

            <h5 className="mb-4">
              Connection Status
            </h5>

            <div className="mb-4">

              {connected ? (
                <span className="badge bg-success">
                  Connected
                </span>
              ) : (
                <span className="badge bg-danger">
                  Not Connected
                </span>
              )}

            </div>

            <table className="table table-borderless">

              <tbody>

                <tr>
                  <th width="40%">Integration</th>
                  <td>DigiTax</td>
                </tr>

                <tr>
                  <th>Status</th>
                  <td>
                    {enabled ? "Enabled" : "Disabled"}
                  </td>
                </tr>

                <tr>
                  <th>Connected</th>
                  <td>
                    {connected ? "Yes" : "No"}
                  </td>
                </tr>

                <tr>
                  <th>Connected At</th>
                  <td>
                    {formatDate(connectedAt)}
                  </td>
                </tr>

                <tr>
                  <th>Last Tested</th>
                  <td>
                    {formatDate(lastTestedAt)}
                  </td>
                </tr>

                <tr>
                  <th>Last Error</th>
                  <td className="text-danger">
                    {lastError || "-"}
                  </td>
                </tr>

              </tbody>

            </table>

            <div className="alert alert-info mt-3 mb-0">

              <strong>What will be synchronized?</strong>

              <ul className="mb-0 mt-2">
                <li>Customers</li>
                <li>Items</li>
                <li>Invoices</li>
              </ul>

            </div>

          </div>
        </div>
      </div>

      {/* DigiTax Profiles */}
      <div className="col-12 mb-3">
        <div className="card">
          <div className="card-body">

            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="mb-1">
                  DigiTax Submission Profiles
                </h5>

                <small className="text-muted">
                  Use profiles when this facility submits invoices under
                  different DigiTax accounts.
                </small>
              </div>

              <span className="badge bg-secondary">
                {profiles.length}{" "}
                {profiles.length === 1 ? "Profile" : "Profiles"}
              </span>
            </div>

            {/* Add Profile */}
            <div className="border rounded p-3 mb-4">

              <h6 className="mb-3">
                Add DigiTax Profile
              </h6>

              <div className="row">

                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Profile Name
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    value={profileName}
                    onChange={(e) =>
                      setProfileName(e.target.value)
                    }
                    placeholder="e.g. Company A"
                    disabled={!enabled}
                  />
                </div>

                <div className="col-md-5 mb-3">
                  <label className="form-label">
                    DigiTax API Key
                  </label>

                  <Password
                    className="w-100"
                    inputClassName="w-100"
                    feedback={false}
                    toggleMask
                    value={profileApiKey}
                    onChange={(e) =>
                      setProfileApiKey(e.target.value)
                    }
                    placeholder="Paste DigiTax API Key"
                    disabled={!enabled}
                  />
                </div>

                <div className="col-md-3 mb-3 d-flex align-items-end">

                  <button
                    type="button"
                    className="btn btn-outline-primary w-100"
                    onClick={handleAddProfile}
                    disabled={!enabled}
                  >
                    Add Profile
                  </button>

                </div>

              </div>

              <small className="text-muted">
                Each profile represents a separate DigiTax submission
                account/API key.
              </small>

            </div>

            {/* Profiles */}
            {profiles.length === 0 ? (

              <div className="text-center text-muted py-4 border rounded">
                <div className="mb-2">
                  No DigiTax profiles configured.
                </div>

                <small>
                  Add profiles above if this facility uses multiple
                  DigiTax submission accounts.
                </small>
              </div>

            ) : (

              <div className="table-responsive">

                <table className="table table-hover align-middle">

                  <thead>
                    <tr>
                      <th>Profile</th>
                      <th>API Key</th>
                      <th>Status</th>
                      <th>Connection</th>
                      <th>Last Tested</th>
                      <th>Last Error</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>

                  <tbody>

                    {profiles.map((profile, index) => (

                      <tr key={profile._id || index}>

                        <td>
                          <strong>
                            {profile.name || "-"}
                          </strong>
                        </td>

                        <td>
                          <Password
                            className="w-100"
                            inputClassName="w-100"
                            feedback={false}
                            toggleMask
                            value={profile.apiKey || ""}
                            readOnly
                          />
                        </td>

                        <td>

                          {profile.enabled ? (
                            <span className="badge bg-success">
                              Enabled
                            </span>
                          ) : (
                            <span className="badge bg-secondary">
                              Disabled
                            </span>
                          )}

                        </td>

                        <td>

                          {profile.connected ? (
                            <span className="badge bg-success">
                              Connected
                            </span>
                          ) : (
                            <span className="badge bg-danger">
                              Not Connected
                            </span>
                          )}

                        </td>

                        <td>
                          {formatDate(profile.lastTestedAt)}
                        </td>

                        <td>
                          {profile.lastError ? (
                            <span
                              className="text-danger"
                              title={profile.lastError}
                            >
                              {profile.lastError.length > 40
                                ? `${profile.lastError.substring(0, 40)}...`
                                : profile.lastError}
                            </span>
                          ) : (
                            <span className="text-muted">
                              -
                            </span>
                          )}
                        </td>

                        <td className="text-end">

                          <div className="d-flex justify-content-end gap-2">

                            <button
                              type="button"
                              className={`btn btn-sm ${profile.enabled
                                ? "btn-outline-warning"
                                : "btn-outline-success"
                                }`}
                              onClick={() =>
                                handleToggleProfile(index)
                              }
                              disabled={!enabled}
                            >
                              {profile.enabled
                                ? "Disable"
                                : "Enable"}
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                handleTestProfile(
                                  profile,
                                  index
                                )
                              }
                              disabled={
                                !enabled ||
                                !profile.enabled ||
                                profileLoading
                              }
                            >
                              Test
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                handleRemoveProfile(index)
                              }
                              disabled={profileLoading}
                            >
                              Remove
                            </button>

                          </div>

                        </td>

                      </tr>

                    ))}

                  </tbody>

                </table>

              </div>

            )}

            <div className="alert alert-warning mt-3 mb-0">

              <strong>Important:</strong>

              <div className="mt-1">
                Profiles are used to determine which DigiTax account
                should receive an invoice. The profile assigned to a
                levy or lease agreement will determine the submission
                account used for that invoice.
              </div>

            </div>

          </div>
        </div>
      </div>

    </div>
  );
};

export default DigitaxTab;
